import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { QuoteView } from "@contracts/index";
import { formatLongDate, formatMoney } from "@presentation/format";
import { labelsFor } from "@presentation/labels";
import { PortalLayout } from "../../components/PortalLayout";
import { clientApi, isUnauthorized } from "../../lib/client-api";
import { browserLanguage, portalText } from "../../lib/i18n";

export function SignOutButton({ label }: { label: string }) {
  const navigate = useNavigate();
  return (
    <button className="link light" onClick={() => void clientApi.logout().finally(() => navigate("/"))}>
      {label}
    </button>
  );
}

/** Quotes sent to the signed-in contact. */
export function MyQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteView[] | null>(null);
  const [email, setEmail] = useState("");
  const language = browserLanguage();
  const text = portalText(language);

  useEffect(() => {
    Promise.all([clientApi.me(), clientApi.listQuotes()])
      .then(([me, list]) => {
        setEmail(me.email);
        setQuotes(list);
      })
      .catch((e: unknown) => {
        if (isUnauthorized(e)) navigate("/", { replace: true });
        else setQuotes([]);
      });
  }, [navigate]);

  return (
    <PortalLayout
      actions={
        <>
          <span className="topbar-user">{email}</span>
          <SignOutButton label={text.signOut} />
        </>
      }
    >
      <section className="card">
        <h1>{text.myQuotes}</h1>
        {quotes === null && <p className="muted">{text.loading}</p>}
        {quotes?.length === 0 && <p className="muted">{text.none}</p>}
        {quotes && quotes.length > 0 && (
          <ul className="quote-cards">
            {quotes.map((q) => {
              const t = labelsFor(q.language);
              return (
                <li key={q.id}>
                  <Link to={`/cotizaciones/${q.id}`}>
                    <span className="strong">
                      {q.folio} · {q.title}
                    </span>
                    <span className="muted small">
                      {q.client.name} · {formatMoney(q.totals.totalCents, q.currency, q.language)} · {t.validUntil}{" "}
                      {formatLongDate(q.validUntil, q.language)}
                    </span>
                    <span className={`badge ${q.expired ? "expired" : q.status}`}>
                      {q.expired ? t.expired : t.status[q.status]}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </PortalLayout>
  );
}
